import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { UserPlus, Building2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const AddTeamMemberDialog = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);
  
  const [formData, setFormData] = useState({
    email: "",
    full_name: "",
    phone: "",
    job_title: "",
    department: "",
    employee_id: "",
    primary_role_id: "",
    subsidiary_ids: [],
    is_active: true,
    send_welcome_email: true,
    require_password_change: true
  });

  useEffect(() => {
    if (open) {
      fetchRoles();
      fetchSubsidiaries();
    }
  }, [open]);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const fetchSubsidiaries = async () => {
    try {
      // Use company_only=true for optimized query
      const response = await axios.get(`${API}/subsidiaries?company_only=true`);
      setSubsidiaries(response.data);
    } catch (error) {
      console.error("Error fetching subsidiaries:", error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubsidiaryToggle = (subsidiaryId) => {
    setFormData(prev => ({
      ...prev,
      subsidiary_ids: prev.subsidiary_ids.includes(subsidiaryId)
        ? prev.subsidiary_ids.filter(id => id !== subsidiaryId)
        : [...prev.subsidiary_ids, subsidiaryId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.email || !formData.full_name || !formData.primary_role_id) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (formData.subsidiary_ids.length === 0) {
      toast.error("Please assign at least one subsidiary");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/team-members/add`, formData);
      toast.success("Team member added successfully");
      
      // Reset form
      setFormData({
        email: "",
        full_name: "",
        phone: "",
        job_title: "",
        department: "",
        employee_id: "",
        primary_role_id: "",
        subsidiary_ids: [],
        is_active: true,
        send_welcome_email: true,
        require_password_change: true
      });
      
      if (onSuccess) onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error("Error adding team member:", error);
      toast.error(error.response?.data?.detail || "Failed to add team member");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add Team Member
          </DialogTitle>
          <DialogDescription>
            Add a new team member to your company. They will receive login credentials.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Personal Information */}
          <div>
            <h3 className="font-medium text-sm mb-3 pb-2 border-b">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => handleChange("full_name", e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="john.doe@company.com"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  placeholder="+1-555-XXX-XXXX"
                />
              </div>
              
              <div>
                <Label htmlFor="job_title">Job Title</Label>
                <Input
                  id="job_title"
                  value={formData.job_title}
                  onChange={(e) => handleChange("job_title", e.target.value)}
                  placeholder="Regional Manager"
                />
              </div>
              
              <div>
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  value={formData.department}
                  onChange={(e) => handleChange("department", e.target.value)}
                  placeholder="Sales"
                />
              </div>
              
              <div>
                <Label htmlFor="employee_id">Employee ID (optional)</Label>
                <Input
                  id="employee_id"
                  value={formData.employee_id}
                  onChange={(e) => handleChange("employee_id", e.target.value)}
                  placeholder="EMP-2025-001"
                />
              </div>
            </div>
          </div>

          {/* Role & Permissions */}
          <div>
            <h3 className="font-medium text-sm mb-3 pb-2 border-b">Role & Permissions</h3>
            <div>
              <Label htmlFor="primary_role_id">Primary Role *</Label>
              <Select value={formData.primary_role_id} onValueChange={(value) => handleChange("primary_role_id", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {roles
                    .filter(role => role.role_scope !== "platform") // Exclude platform roles
                    .map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Subsidiary Assignment */}
          <div>
            <h3 className="font-medium text-sm mb-3 pb-2 border-b flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Subsidiary Assignment *
            </h3>
            <p className="text-sm text-slate-600 mb-3">
              Select subsidiaries this member will work with
            </p>
            <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
              {subsidiaries.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No subsidiaries available</p>
              ) : (
                subsidiaries.map((sub) => (
                  <div key={sub.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`sub-${sub.id}`}
                      checked={formData.subsidiary_ids.includes(sub.id)}
                      onCheckedChange={() => handleSubsidiaryToggle(sub.id)}
                    />
                    <Label htmlFor={`sub-${sub.id}`} className="text-sm cursor-pointer">
                      {sub.name} ({sub.code || sub.location})
                    </Label>
                  </div>
                ))
              )}
            </div>
            {formData.subsidiary_ids.length > 0 && (
              <p className="text-xs text-slate-500 mt-2">
                Selected: {formData.subsidiary_ids.length} subsidiaries
              </p>
            )}
          </div>

          {/* Account Settings */}
          <div>
            <h3 className="font-medium text-sm mb-3 pb-2 border-b">Account Settings</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => handleChange("is_active", checked)}
                />
                <Label htmlFor="is_active" className="text-sm cursor-pointer">
                  Active (user can log in immediately)
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send_welcome_email"
                  checked={formData.send_welcome_email}
                  onCheckedChange={(checked) => handleChange("send_welcome_email", checked)}
                />
                <Label htmlFor="send_welcome_email" className="text-sm cursor-pointer">
                  Send welcome email with login instructions
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="require_password_change"
                  checked={formData.require_password_change}
                  onCheckedChange={(checked) => handleChange("require_password_change", checked)}
                />
                <Label htmlFor="require_password_change" className="text-sm cursor-pointer">
                  Require password change on first login
                </Label>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Adding..." : "Add Team Member"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTeamMemberDialog;
